import React, { useEffect, useState } from "react";
import { Boxes, Trash2, Pencil, Flower2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, rupiah } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, Button, Badge } from "@/components/common";
import MaterialUsageModal from "@/components/MaterialUsageModal";

export default function MaterialUsage() {
  const { branch, isOwner } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [editUsage, setEditUsage] = useState(null);

  const load = async () => {
    if (!branch) return;
    setLoading(true);
    try { setRows(await api.get("/production/usage", { branch_id: branch.id, limit: 200 })); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [branch]);

  const del = async (u) => {
    if (!window.confirm(`Hapus pemakaian ${u.usage_no}? Stok bahan akan dikembalikan.`)) return;
    try { await api.delete(`/production/usage/${u.id}`); toast.success("Pemakaian dihapus, stok dikembalikan"); load(); }
    catch (e) { toast.error(e.data?.detail || e.message); }
  };

  if (loading) return <Loading label="Memuat pemakaian rangkaian..." />;

  return (
    <>
      <PageHeader title="Pemakaian Rangkaian" subtitle="Bahan baku, perlengkapan & bunga per tangkai yang terpakai (terhubung stok)" icon={Boxes} />
      <div className="text-sm text-slate-500 mb-4 flex items-center gap-1.5"><Flower2 size={15} /> Setiap pencatatan otomatis mengurangi stok (FIFO). {isOwner ? "Owner dapat menambah input yang kurang (Edit) atau menghapus." : "Edit & hapus hanya oleh Owner."}</div>

      {rows.length === 0 ? (
        <EmptyState icon={Boxes} title="Belum ada pemakaian tercatat" message="Pemakaian bahan tercatat saat perangkai menyelesaikan rangkaian di Order Center / Papan Pengerjaan." />
      ) : (
        <div className="space-y-3">
          {rows.map((u) => (
            <Card key={u.id} data-testid={`musage-${u.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800">{u.usage_no}</span>
                    <Badge className={u.type === "order" ? "bg-primary/10 text-primary" : "bg-sky-100 text-sky-700"}>{u.type === "order" ? `Order ${u.order_no || ""}` : `Display: ${u.product_name || ""}`}</Badge>
                    <span className="text-sm font-medium text-slate-700">{rupiah(u.total_cost)}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">{u.created_by_name} · {fmtDateTime(u.created_at)} · {u.lines?.length || 0} bahan</div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(u.lines || []).map((l, i) => (
                      <span key={i} className="text-xs bg-slate-100 text-slate-600 rounded-lg px-2 py-1" data-testid={`musage-line-${u.id}-${i}`}>{l.name} · {l.qty} {l.unit}</span>
                    ))}
                  </div>
                </div>
                {isOwner && (
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button variant="secondary" size="sm" onClick={() => setEditUsage(u)} data-testid={`musage-edit-${u.id}`}><Pencil size={14} /> Edit</Button>
                    <button onClick={() => del(u)} className="text-xs text-rose-600 inline-flex items-center gap-1 justify-center" data-testid={`musage-del-${u.id}`}><Trash2 size={13} /> Hapus</button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {editUsage && <MaterialUsageModal editUsage={editUsage} onClose={() => setEditUsage(null)} onSaved={() => { setEditUsage(null); load(); }} />}
    </>
  );
}
